import { CLEF_INFO, GRAND_STAFF_INFO, grandStaffClefFor, isClef, isGrandStaffClef, isLinePosition, staffPosition, type Clef } from '../../clef.ts';
import {
  ACCIDENTALS,
  compareSpelled,
  diatonicIndex,
  fromDiatonicIndex,
  isAccidental,
  isRareSpelling,
  isWithinRange,
  parsePitchId,
  pitchId,
  pitchLabel,
  type Accidental,
  type SpelledPitch,
} from '../../pitch.ts';
import type { ValidationIssue, ValidationResult } from '../types.ts';

export const ANSWER_MODES = ['letter', 'spelled', 'spelled-octave'] as const;
export type AnswerMode = (typeof ANSWER_MODES)[number];

export const ANSWER_MODE_LABEL: Record<AnswerMode, string> = {
  letter: 'Letter name (A–G)',
  spelled: 'Note name with accidental',
  'spelled-octave': 'Note name with octave number',
};

export const STAFF_FILTERS = ['all', 'lines', 'spaces'] as const;
export type StaffFilter = (typeof STAFF_FILTERS)[number];

export const QUESTION_COUNT_MIN = 3;
export const QUESTION_COUNT_MAX = 50;

/**
 * Persisted note-identification configuration. Pitches are stored as stable
 * ASCII ids ("C4", "F#5") so configs are plain JSON.
 */
export interface NoteIdConfig {
  /** The clef, or with `grandStaff`, "treble" by convention (each note's staff comes from its pitch). */
  clef: Clef;
  /**
   * Show notes on a grand staff and draw from both treble and bass: pitches from
   * middle C up go on the treble staff, lower ones on the bass staff. Each
   * question still records its own staff's clef, so grading and skills are
   * unchanged. Range limits are the grand staff's (bass low to treble high).
   */
  grandStaff?: boolean | undefined;
  /** Inclusive written range, ordered by staff position then alteration (see `compareSpelled`). */
  range: { low: string; high: string };
  /** Accidentals that may appear. `['natural']` means naturals only. */
  accidentals: Accidental[];
  answerMode: AnswerMode;
  questionCount: number;
  /** Restrict to lines or spaces (used by lessons). Default "all". */
  staffFilter: StaffFilter;
  /**
   * Explicit staff positions used by lessons ("E, G and B"), written as natural pitches.
   * When present, questions use only these positions, each combined with the allowed
   * accidentals, and still filtered by range and staffFilter.
   */
  pool?: string[] | undefined;
  /** Share of questions that carry an accidental when accidentals are enabled (0–1). */
  accidentalRate: number;
  /** Allow E#, B#, Fb and Cb. Off by default. */
  includeRareSpellings: boolean;
  hintsAllowed: boolean;
}

export const DEFAULT_ACCIDENTAL_RATE = 0.5;

export function defaultNoteIdConfig(clef: Clef): NoteIdConfig {
  const range = CLEF_INFO[clef].defaultRange;
  return {
    clef,
    range: { low: pitchId(range.low), high: pitchId(range.high) },
    accidentals: ['natural'],
    answerMode: 'letter',
    questionCount: 10,
    staffFilter: 'all',
    accidentalRate: DEFAULT_ACCIDENTAL_RATE,
    includeRareSpellings: false,
    hintsAllowed: true,
  };
}

export function defaultGrandStaffConfig(): NoteIdConfig {
  const range = GRAND_STAFF_INFO.defaultRange;
  return { ...defaultNoteIdConfig('treble'), grandStaff: true, range: { low: pitchId(range.low), high: pitchId(range.high) } };
}

/** Clef of the staff a pitch is written on under this configuration. */
export function staffClefFor(config: Pick<NoteIdConfig, 'clef' | 'grandStaff'>, pitchValue: Pick<SpelledPitch, 'letter' | 'octave'>): Clef {
  return config.grandStaff ? grandStaffClefFor(pitchValue) : config.clef;
}

/**
 * All distinct spelled pitches a configuration can produce. This is the
 * candidate set for generation and the basis of several validation rules.
 * Assumes the configuration is already structurally valid.
 */
export function candidatePitches(config: NoteIdConfig): SpelledPitch[] {
  const low = parsePitchId(config.range.low);
  const high = parsePitchId(config.range.high);
  if (!low || !high) return [];
  const allowed = new Set(config.accidentals);

  // Staff positions to draw from: the explicit pool (natural staff notes) or every step in range.
  const steps: number[] = [];
  if (config.pool && config.pool.length > 0) {
    for (const id of config.pool) {
      const parsed = parsePitchId(id);
      if (parsed) steps.push(diatonicIndex(parsed));
    }
  } else {
    for (let index = diatonicIndex(low); index <= diatonicIndex(high); index++) steps.push(index);
  }
  const base: SpelledPitch[] = [];
  for (const index of steps) {
    for (const accidental of ACCIDENTALS) base.push(fromDiatonicIndex(index, accidental));
  }

  const seen = new Set<string>();
  const out: SpelledPitch[] = [];
  for (const candidate of base) {
    if (!allowed.has(candidate.accidental)) continue;
    if (!isWithinRange(candidate, low, high)) continue;
    if (!config.includeRareSpellings && isRareSpelling(candidate)) continue;
    const pos = staffPosition(staffClefFor(config, candidate), candidate);
    if (config.staffFilter === 'lines' && !isLinePosition(pos)) continue;
    if (config.staffFilter === 'spaces' && isLinePosition(pos)) continue;
    const id = pitchId(candidate);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(candidate);
  }
  return out.sort(compareSpelled);
}

function issue(path: string, code: string, message: string): ValidationIssue {
  return { path, code, message };
}

/**
 * Structural + musical validation. Returns a normalized config (defaults
 * applied) or a list of issues suitable for showing next to teacher form fields.
 */
export function validateNoteIdConfig(input: unknown): ValidationResult<NoteIdConfig> {
  const issues: ValidationIssue[] = [];
  if (typeof input !== 'object' || input === null) {
    return { ok: false, issues: [issue('', 'invalid_type', 'Configuration must be an object.')] };
  }
  const raw = input as Record<string, unknown>;

  const clef = typeof raw.clef === 'string' && isClef(raw.clef) ? raw.clef : null;
  if (!clef) issues.push(issue('clef', 'invalid_clef', 'Choose treble, bass, alto or tenor clef.'));
  const grandStaff = raw.grandStaff === undefined || raw.grandStaff === null ? false : raw.grandStaff;
  if (typeof grandStaff !== 'boolean') issues.push(issue('grandStaff', 'invalid_type', 'Expected true or false.'));
  if (grandStaff === true && clef && !isGrandStaffClef(clef)) {
    issues.push(issue('clef', 'invalid_clef', 'The grand staff combines treble and bass clef.'));
  }

  const rangeRaw = (raw.range ?? {}) as Record<string, unknown>;
  const low = typeof rangeRaw.low === 'string' ? parsePitchId(rangeRaw.low) : null;
  const high = typeof rangeRaw.high === 'string' ? parsePitchId(rangeRaw.high) : null;
  if (!low) issues.push(issue('range.low', 'invalid_pitch', 'Choose a valid lowest note.'));
  if (!high) issues.push(issue('range.high', 'invalid_pitch', 'Choose a valid highest note.'));
  if (low && high && compareSpelled(low, high) > 0) {
    issues.push(issue('range', 'range_inverted', 'The lowest note must not be above the highest note.'));
  }
  if (clef && low && high) {
    const grand = grandStaff === true;
    const practical = grand ? GRAND_STAFF_INFO.practicalRange : CLEF_INFO[clef].practicalRange;
    const where = grand ? 'On the grand staff' : `In ${CLEF_INFO[clef].name.toLowerCase()}`;
    if (compareSpelled(low, practical.low) < 0 || compareSpelled(low, practical.high) > 0) {
      issues.push(
        issue(
          'range.low',
          'out_of_clef_range',
          `${where} the range must stay between ${pitchLabel(practical.low)} and ${pitchLabel(practical.high)} (three ledger lines).`,
        ),
      );
    }
    if (compareSpelled(high, practical.low) < 0 || compareSpelled(high, practical.high) > 0) {
      issues.push(
        issue(
          'range.high',
          'out_of_clef_range',
          `${where} the range must stay between ${pitchLabel(practical.low)} and ${pitchLabel(practical.high)} (three ledger lines).`,
        ),
      );
    }
  }

  let accidentals: Accidental[] = [];
  if (!Array.isArray(raw.accidentals) || raw.accidentals.length === 0) {
    issues.push(issue('accidentals', 'invalid_accidentals', 'Choose at least one accidental option.'));
  } else if (!raw.accidentals.every((a) => typeof a === 'string' && isAccidental(a))) {
    issues.push(issue('accidentals', 'unsupported_accidental', 'Only naturals, sharps and flats are supported.'));
  } else {
    accidentals = ACCIDENTALS.filter((a) => (raw.accidentals as string[]).includes(a));
  }

  const answerMode =
    typeof raw.answerMode === 'string' && (ANSWER_MODES as readonly string[]).includes(raw.answerMode)
      ? (raw.answerMode as AnswerMode)
      : null;
  if (!answerMode) issues.push(issue('answerMode', 'invalid_answer_mode', 'Choose an answer mode.'));

  if (answerMode === 'letter' && accidentals.some((a) => a !== 'natural')) {
    issues.push(
      issue(
        'answerMode',
        'ambiguous_letter_mode',
        'Letter-only answers cannot be used with sharps or flats, because C♯ and C would both be answered “C”. Use “Note name with accidental” instead.',
      ),
    );
  }

  const questionCount = raw.questionCount;
  if (
    typeof questionCount !== 'number' ||
    !Number.isInteger(questionCount) ||
    questionCount < QUESTION_COUNT_MIN ||
    questionCount > QUESTION_COUNT_MAX
  ) {
    issues.push(
      issue(
        'questionCount',
        'invalid_question_count',
        `Question count must be a whole number from ${QUESTION_COUNT_MIN} to ${QUESTION_COUNT_MAX}.`,
      ),
    );
  }

  const staffFilter =
    raw.staffFilter === undefined
      ? 'all'
      : typeof raw.staffFilter === 'string' && (STAFF_FILTERS as readonly string[]).includes(raw.staffFilter)
        ? (raw.staffFilter as StaffFilter)
        : null;
  if (!staffFilter) issues.push(issue('staffFilter', 'invalid_staff_filter', 'Choose all notes, lines only, or spaces only.'));

  let pool: string[] | undefined;
  if (raw.pool !== undefined && raw.pool !== null) {
    if (!Array.isArray(raw.pool) || !raw.pool.every((x) => typeof x === 'string' && parsePitchId(x))) {
      issues.push(issue('pool', 'invalid_pool', 'The note set contains an invalid note.'));
    } else if (!raw.pool.every((x) => parsePitchId(x as string)?.accidental === 'natural')) {
      issues.push(issue('pool', 'invalid_pool', 'List note-set staff positions as natural notes; accidentals come from the accidental setting.'));
    } else {
      pool = (raw.pool as string[]).map((id) => pitchId(parsePitchId(id) as SpelledPitch));
    }
    if (grandStaff === true) issues.push(issue('pool', 'invalid_pool', 'Note sets are for single-clef lessons, not the grand staff.'));
  }

  const accidentalRate = raw.accidentalRate === undefined ? DEFAULT_ACCIDENTAL_RATE : raw.accidentalRate;
  if (typeof accidentalRate !== 'number' || accidentalRate < 0 || accidentalRate > 1) {
    issues.push(issue('accidentalRate', 'invalid_rate', 'Accidental share must be between 0 and 1.'));
  }

  const includeRareSpellings = raw.includeRareSpellings === undefined ? false : raw.includeRareSpellings;
  if (typeof includeRareSpellings !== 'boolean') {
    issues.push(issue('includeRareSpellings', 'invalid_type', 'Expected true or false.'));
  }
  const hintsAllowed = raw.hintsAllowed === undefined ? true : raw.hintsAllowed;
  if (typeof hintsAllowed !== 'boolean') issues.push(issue('hintsAllowed', 'invalid_type', 'Expected true or false.'));

  if (issues.length > 0) return { ok: false, issues };

  const config: NoteIdConfig = {
    // A grand-staff config stores "treble" by convention; each note's staff comes from its pitch.
    clef: grandStaff === true ? 'treble' : (clef as Clef),
    ...(grandStaff === true ? { grandStaff: true } : {}),
    range: { low: pitchId(low as SpelledPitch), high: pitchId(high as SpelledPitch) },
    accidentals,
    answerMode: answerMode as AnswerMode,
    questionCount: questionCount as number,
    staffFilter: staffFilter as StaffFilter,
    ...(pool ? { pool } : {}),
    accidentalRate: accidentalRate as number,
    includeRareSpellings: includeRareSpellings as boolean,
    hintsAllowed: hintsAllowed as boolean,
  };

  const candidates = candidatePitches(config);
  const distinctAnswers = new Set(
    candidates.map((c) =>
      config.answerMode === 'letter'
        ? c.letter
        : config.answerMode === 'spelled'
          ? `${c.letter}${c.accidental}`
          : pitchId(c),
    ),
  );
  if (distinctAnswers.size < 2) {
    issues.push(
      issue(
        'range',
        'too_few_notes',
        'This combination produces fewer than two different answers. Widen the range or allow more note types.',
      ),
    );
  }
  if (accidentals.some((a) => a !== 'natural') && !candidates.some((c) => c.accidental !== 'natural')) {
    issues.push(issue('accidentals', 'no_accidentals_in_range', 'No sharps or flats fit inside this range.'));
  }
  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, value: config };
}

export function describeNoteIdConfig(config: NoteIdConfig): string {
  const low = parsePitchId(config.range.low);
  const high = parsePitchId(config.range.high);
  const rangeText = low && high ? `${pitchLabel(low)}–${pitchLabel(high)}` : 'custom range';
  const acc = config.accidentals.filter((a) => a !== 'natural');
  const accText = acc.length === 0 ? 'naturals only' : `with ${acc.map((a) => (a === 'sharp' ? 'sharps' : 'flats')).join(' and ')}`;
  const filter = config.staffFilter === 'all' ? '' : config.staffFilter === 'lines' ? ', lines only' : ', spaces only';
  const staffName = config.grandStaff ? GRAND_STAFF_INFO.name : CLEF_INFO[config.clef].name;
  return `${staffName}, ${rangeText}, ${accText}${filter} · ${config.questionCount} questions`;
}
